import { BaseInformation } from '@/models/games/BaseInformation';
import { BaseResult } from '@/models/games/BaseResult';
import {
    Blackjack2GameInfo,
    Blackjack2OwnResult,
    Blackjack2Result,
} from '@/models/games/blackjack2';
import { DecisionState } from '@/models/games/blackjack2/enums/DecisionState';
import { GameState as Blackjack2GameState } from '@/models/games/blackjack2/enums/GameState';
import {
    convertBJ2Hands,
    OldResultInfo,
    ResultInfo,
} from '@/models/games/blackjack2/GameInfo';
import { GameRecord } from '@/models/games/blackjack2/GameRecord';
import { GameStateInfo } from '@/models/games/blackjack2/GameResult';
import { BetSeatIndex } from '@/models/games/blackjack2/SeatPlayerInfo';
import { GameState } from '@/models/games/enums/GameState';
import { GameType } from '@/models/games/enums/GameType';
import { TableMode } from '@/models/TableMode';
import { tableBetSliceActions } from '@/modules/betTable/slice';
import { SupportedGameLiveData } from '@/modules/hostLive/models';
import { hostLiveSliceActions } from '@/modules/hostLive/slice';
import { getMainUserState } from '@/modules/main/selector';
import { bj2HandSliceActions } from '@/modules/rwdGame/blackjack2/slice/handSlice';
import {
    bj2SeatSliceActions,
    getEmptyBJ2Seats,
} from '@/modules/rwdGame/blackjack2/slice/seatSlice';
import store from '@/store/store';
import { getIsRestState, updateRemainTime } from '@/utils/commonFunc';
import { convertResultToRecord } from '@/utils/games/blackjack2';
import { Action, Dispatch } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { SupportedGame } from '../../../modules/host/models';
import { hostsSliceActions } from '../../../modules/host/slice';
import { CMDScInitNewBlackjack, ICommand } from '../live';
import { CMDBaseInformation } from '../live/CMDBaseInformation';
import { AbstractHandler } from './AbstractHandler';

export class CMDScInitNewBlackjackHandler extends AbstractHandler {
    _command: CMDScInitNewBlackjack;
    constructor(command: ICommand) {
        super();
        this._command = command as CMDScInitNewBlackjack;
    }

    convertBJ2Information(
        information: CMDBaseInformation,
        result: Blackjack2GameInfo
    ): BaseInformation {
        const newInformation = {} as BaseInformation;
        newInformation.AllowSideBet = information.AllowSideBet === 1;
        newInformation.CountDown = result.count_down * 1000;
        newInformation.HallId = information.HallID;
        newInformation.HostId = information.HostID;
        newInformation.RemainTime = updateRemainTime(result.remain_time);
        newInformation.Rest = information.Rest;
        newInformation.Seat = information.Seat;
        newInformation.TableLimit = information.TableLimit;
        return newInformation;
    }
    convertBJ2Result(
        game_info: Blackjack2GameInfo,
        ResultString: string
    ): Blackjack2Result {
        // convert index start from 1 to 0
        const baseResult: BaseResult = {
            FResult: '',
            Result: 0,
            GameCount: game_info.game_count,
            GameID: Number(game_info.game_id),
        };
        const ownResult: Blackjack2OwnResult = {
            dealer: game_info.dealer,
            seatInfos: game_info.seats_info,
        };
        const gameInfo: GameStateInfo = {
            state: game_info.state,
            subState: game_info.sub_state,
        };
        const other = {
            ResultString: ResultString,
        };
        const newResult = Object.assign(
            baseResult,
            ownResult,
            gameInfo,
            other
        ) as Blackjack2Result;
        return newResult;
    }
    override handle(dispatch: Dispatch<Action>): void {
        const state = store.getState();
        const { IsInternalTest } = getMainUserState(state);
        const game = {} as SupportedGame;
        const liveData = {} as SupportedGameLiveData;
        const game_info = JSON.parse(this._command.JSON) as Blackjack2GameInfo;
        const result = this.convertBJ2Result(game_info, this._command.JSON);
        const info = this.convertBJ2Information(
            this._command.Information,
            game_info
        );
        const hostId = info.HostId;
        const multiplier = this._command.Multipier; // tmp
        liveData.GameType = GameType.Blackjack2;
        liveData.CurrentState =
            result.state === Blackjack2GameState.Ready
                ? GameState.Betting
                : result.state;
        liveData.CurrentResult = result;
        const restMode = getIsRestState(
            this._command.Information.Rest,
            IsInternalTest
        );
        game.IsRest = restMode.isRest;
        game.IsPause = restMode.isPause;
        game.IsInternalTest =
            IsInternalTest &&
            this._command.Information.Rest == TableMode.InternalTest;

        // add records
        // if (liveData.CurrentState !== GameState.Shuffle) {
        //     for (const or of this._command.OldResult) {
        //         const oldResult = this.convertResult(or);
        //         const oldRecord = convertResultToRecord(
        //             oldResult,
        //             oldResult.ResultString ?? '',
        //             oldResult.Result
        //         );
        //         if (!records.find(r => r.gameID == oldRecord.gameID))
        //             records.push(oldRecord);
        //     }
        // }

        let records = [] as Array<GameRecord>;
        if (liveData.CurrentState !== GameState.Shuffle) {
            records = this._command.OldResult.map(o => {
                const json = JSON.parse(o) as OldResultInfo;
                return convertResultToRecord(
                    {
                        ...json,
                    } as ResultInfo,
                    Number(json.gameid)
                );
            });
        }
        // lobby info
        const seatInfo = getEmptyBJ2Seats(hostId);
        const handSeat = convertBJ2Hands(this._command.JSON, hostId);

        const seats_info = handSeat.others.seats_info;
        for (let i = 0; i < seats_info.length; i++) {
            let key = `Seat${seats_info[i].seat_num}` as BetSeatIndex;
            let decision = DecisionState.NO_DECISION;
            if (
                seats_info[i].seat_num > handSeat.others.action_seat &&
                seats_info[i].allow_decision.length > 0 &&
                handSeat.others.action_seat !== 0
            ) {
                decision = DecisionState.PRE_DECISION;
            } else if (handSeat.others.state === Blackjack2GameState.Decision) {
                decision = DecisionState.NOW_DECISION;
            }
            if (
                handSeat.others.state === Blackjack2GameState.Started ||
                handSeat.others.state === Blackjack2GameState.Betting ||
                handSeat.others.state === Blackjack2GameState.BetTicketing ||
                handSeat.others.state ===
                    Blackjack2GameState.ShowDealerSecondCard ||
                handSeat.others.state === Blackjack2GameState.GameResult
            ) {
                decision = DecisionState.NO_DECISION;
            }
            seatInfo[key].IsSplit = seats_info[i].is_split == 1;
            seatInfo[key].PreDecision = seats_info[i].predecision;
            seatInfo[key].PlayerID = seats_info[i].player_id;
            seatInfo[key].DecisionState = decision;
        }
        batch(() => {
            dispatch(
                tableBetSliceActions.initTable({
                    hostId: hostId,
                    gameId: result.GameID,
                })
            );
            console.log('???', info.CountDown, info.HostId);
            dispatch(hostLiveSliceActions.gameInit({ hostId, game, liveData }));
            dispatch(hostLiveSliceActions.updateInformation(info));
            dispatch(hostsSliceActions.gameInit({ hostId, game, liveData }));
            dispatch(hostsSliceActions.updateInformation(info));
            dispatch(
                hostsSliceActions.updateMultiplier({
                    hostId,
                    multiplier,
                })
            );
            dispatch(bj2SeatSliceActions.init(seatInfo));
            dispatch(bj2HandSliceActions.init(handSeat.infos));
            dispatch(
                hostsSliceActions.updateRecord({
                    hostId,
                    record: records,
                })
            );
        });
    }
}
